import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ResponseData } from '@freelancer/datastore';
import { FACEBOOK_CONFIG } from './facebook.config';
import { FacebookConfig } from './facebook.interface';

@Injectable()
export class Facebook {
  isEnabled = false;
  loadedPromise: Promise<void>;

  constructor(
    @Inject(FACEBOOK_CONFIG) private config: FacebookConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (this.config.appId && isPlatformBrowser(this.platformId)) {
      this.isEnabled = true;
    }
  }

  get appId() {
    return this.config.appId;
  }

  async login(): Promise<ResponseData<fb.AuthResponse, fb.LoginStatus>> {
    if (!this.isEnabled) {
      return Promise.resolve({
        status: 'error',
        errorCode: 'unknown',
      });
    }

    await this.loadSdk();

    const fbResponse = await new Promise<fb.StatusResponse>(
      (resolve, reject) => {
        window.FB.login((response: fb.StatusResponse) => {
          resolve(response);
        });
      },
    );

    if (fbResponse.status === 'connected' && fbResponse.authResponse) {
      return {
        status: 'success',
        result: fbResponse.authResponse,
      };
    }
    return {
      status: 'error',
      errorCode: fbResponse.status,
    };
  }

  private loadSdk(): Promise<void> {
    if (this.loadedPromise) {
      return this.loadedPromise;
    }

    this.loadedPromise = new Promise((resolve, reject) => {
      const { appId } = this.config;
      if (!appId || !this.isEnabled) {
        resolve();
        return;
      }
      window.fbAsyncInit = () => {
        window.FB.init({
          appId,
          autoLogAppEvents: true,
          xfbml: false,
          cookie: false,
          status: true,
          version: 'v3.2',
        });
        resolve();
      };
      if (document.getElementById('external-fb-sdk')) {
        return;
      }
      const e = document.createElement('script');
      e.src = 'https://connect.facebook.net/en_US/sdk.js';
      e.id = 'external-fb-sdk';
      e.onerror = reject;
      const s = document.getElementsByTagName('script')[0];
      (s.parentNode as Node).insertBefore(e, s);
    });
    return this.loadedPromise;
  }
}
